import * as React from 'react';

import {
  DescriptionPage_CourseType as CourseType,
  DescriptionPage_MarketingSource as MarketingSource,
} from '__generated__/graphql-types';

import { useLocation, useParams } from 'js/lib/useRouter';

import { redirectClientOrServer } from 'bundles/common/utils/urlUtils';
import { useGenerateStartFreeTrialTestEpicImpression } from 'bundles/coursera-plus-common/components/elevateLTV/startFreeTrial/useGenerateStartFreeTrialTestEpicImpression';
import useGenerateGooglePriceTestImpression from 'bundles/payments/utils/useGenerateGooglePriceTestImpression';
import { CoursePageCourseRichSearchCard } from 'bundles/seo/components/course-rich-search-card/CoursePageCourseRichSearchCard';
import getProductTitle from 'bundles/unified-common/utils/getProductTitle';
import AdminBannerNotConfigurable from 'bundles/unified-description-page-common/components/admin/AdminBannerNotConfigurable';
import ConsumerCourseEnrollButtonNotConfigurable from 'bundles/unified-description-page-common/components/enroll/consumer/ConsumerCourseEnrollButtonNotConfigurable';
import MobileCtaModule from 'bundles/unified-description-page-common/components/modules/mobile-cta/MobileCtaModule';
import FixedPageNavigation from 'bundles/unified-description-page-common/components/modules/page-navigation/FixedPageNavigation';
import NavPartnerLogo from 'bundles/unified-description-page-common/components/modules/page-navigation/NavPartnerLogo';
import CoursePageSEO from 'bundles/unified-description-page-common/components/seo/CoursePageSEO';
import CoBrandingPartnerLogos from 'bundles/unified-description-page-common/components/shared/CoBrandingPartnerLogos';
import { useCoursePrice } from 'bundles/unified-description-page-common/contexts/CoursePriceContext';
import { useCollections } from 'bundles/unified-description-page-common/contexts/DegreesCollectionsContext';
import { useCoursePageData } from 'bundles/unified-description-page-common/contexts/consumer/ConsumerBffCourseContext';
import useLogRecentlyViewed from 'bundles/unified-description-page-common/hooks/useRecentlyViewed';
import Renderer from 'bundles/unified-description-page-common/pages/consumer/course/ConsumerCoursePaidMediaPageRenderer';
import EventingDataProvider from 'bundles/unified-description-page-common/providers/EventingDataProvider';
import LanguageInfoDialogProvider from 'bundles/unified-description-page-common/providers/LanguageInfoDialogProvider';
import PageInfoProvider from 'bundles/unified-description-page-common/providers/PageInfoProvider';
import PageNavigationProvider from 'bundles/unified-description-page-common/providers/PageNavigationProvider';
import StickyPanelProvider from 'bundles/unified-description-page-common/providers/StickyPanelProvider';
import TranslationsNotificationBannerProvider from 'bundles/unified-description-page-common/providers/consumer/TranslationsNotificationBannerProvider';
import { ModuleNames } from 'bundles/unified-description-page-common/types/moduleConfig';
import {
  COURSE_NAV_ITEMS,
  getCoursePageNavigationLinks,
} from 'bundles/unified-description-page-common/utils/getPageNavigationLinks';
import getPartnersForCourse from 'bundles/unified-description-page-common/utils/getPartnersForCourse';
import getProductId from 'bundles/unified-description-page-common/utils/getProductId';
import { getConsumerRouteForCourseProducts } from 'bundles/unified-description-page-common/utils/getRouteForCourseProducts';
import getVariantTitle from 'bundles/unified-description-page-common/utils/getVariantTitle';

const ConsumerCoursePaidMediaPageRootContent = () => {
  const pageData = useCoursePageData();
  const collections = useCollections();
  const { search } = useLocation();
  const coursePrice = useCoursePrice();
  const productId = getProductId('course', pageData?.id);
  const productSlugInUrl = useParams().productSlug;

  useLogRecentlyViewed({
    name: pageData?.name,
    slug: pageData?.slug,
    partners: pageData?.partners,
    // @ts-expect-error URL: string
    photoUrl: pageData?.photoUrl,
    id: productId,
    difficulty: pageData?.difficultyLevel,
    productType: pageData?.courseType,
  });

  useGenerateGooglePriceTestImpression({
    hasSpecialization: Boolean(pageData?.primarySpecialization),
    partners: pageData?.partners,
  });

  useGenerateStartFreeTrialTestEpicImpression({ isStandaloneCourse: !pageData?.primarySpecialization });

  if (!pageData) {
    return null;
  } else if (
    !!pageData.courseType &&
    (pageData.courseType !== CourseType.StandardCourse || productSlugInUrl !== pageData.slug)
  ) {
    redirectClientOrServer(getConsumerRouteForCourseProducts(pageData.slug, pageData.courseType, search));
  }

  const partnersToDisplay = getPartnersForCourse(pageData.partners);
  const productTitle = getProductTitle(pageData.name);

  const pageConfig = {
    modules: [
      { name: ModuleNames.PageBanner },
      {
        name: ModuleNames.ProductOverview,
      },
      {
        name: ModuleNames.CareerOutcomes,
      },
      {
        name: ModuleNames.LearnerIncentive,
      },
      {
        name: ModuleNames.Syllabus,
      },
      {
        name: ModuleNames.Testimonials,
      },
      {
        name: ModuleNames.Reviews,
      },
      {
        name: ModuleNames.Faqs,
      },
      {
        name: ModuleNames.AiDisclaimer,
      },
    ],
  };

  const navigationLinks = getCoursePageNavigationLinks({
    pageData,
    collections,
    marketingSource: MarketingSource.PaidMedia,
    pageConfig,
  });

  return (
    <PageInfoProvider marketingSource={MarketingSource.PaidMedia} navigationIdMap={COURSE_NAV_ITEMS}>
      <EventingDataProvider productId={pageData.id} productType="course">
        <PageNavigationProvider navigationLinks={navigationLinks}>
          <StickyPanelProvider>
            <LanguageInfoDialogProvider
              languages={{
                primaryLanguages: pageData.primaryLanguages,
                subtitleLanguages: pageData.subtitleLanguages ?? [],
                translatedLanguages: pageData.translatedLanguages ?? [],
              }}
            >
              <TranslationsNotificationBannerProvider
                translatedLanguages={pageData.translatedLanguages}
                productType={getVariantTitle()}
                // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
                originalLanguage={pageData.primaryLanguages[0]}
              >
                <AdminBannerNotConfigurable
                  courseId={pageData.id}
                  slug={pageData.slug}
                  courseStatus={pageData.courseStatus}
                />
                <FixedPageNavigation
                  productTitle={productTitle}
                  navigationLinks={navigationLinks}
                  pageCta={
                    <ConsumerCourseEnrollButtonNotConfigurable pageData={pageData} size="small" onlyShowButton />
                  }
                  partnerLogo={
                    partnersToDisplay && partnersToDisplay.length > 1 ? (
                      <CoBrandingPartnerLogos partners={partnersToDisplay} isNavPlacement={true} />
                    ) : (
                      <NavPartnerLogo
                        // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
                        imageSrc={partnersToDisplay?.[0].squareLogo}
                        // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
                        partnerName={partnersToDisplay?.[0].name}
                      />
                    )
                  }
                />
                <Renderer pageData={pageData} pageConfig={pageConfig} />
                <MobileCtaModule
                  pageCta={
                    <ConsumerCourseEnrollButtonNotConfigurable pageData={pageData} size="small" onlyShowButton />
                  }
                />
                <CoursePageSEO pageData={pageData} disableCrawlerIndexing />
                {coursePrice && <CoursePageCourseRichSearchCard pageData={pageData} coursePrice={coursePrice} />}
              </TranslationsNotificationBannerProvider>
            </LanguageInfoDialogProvider>
          </StickyPanelProvider>
        </PageNavigationProvider>
      </EventingDataProvider>
    </PageInfoProvider>
  );
};

export default ConsumerCoursePaidMediaPageRootContent;
